package com.patronat.top.client.presentation.saved

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.patronat.top.client.presentation.home.components.ratingSummaryText
import com.patronat.top.client.presentation.home.model.labelRes
import com.patronat.top.dto.VenueResponse
import com.patronat.top.ui.atoms.AppNetworkImage
import com.patronat.top.ui.atoms.AppSearchField
import com.patronat.top.ui.theme.AppIcons
import com.patronat.top.ui.theme.BodyLarge
import com.patronat.top.ui.theme.BodyMedium
import com.patronat.top.ui.theme.MineShaft100
import com.patronat.top.ui.theme.MineShaft50
import com.patronat.top.ui.theme.MineShaft500
import com.patronat.top.ui.theme.MineShaft600
import com.patronat.top.ui.theme.MineShaft900
import com.patronat.top.ui.theme.MineShaft950
import com.patronat.top.ui.theme.Primary600
import com.patronat.top.ui.theme.White
import org.jetbrains.compose.resources.stringResource
import top.clientapp.generated.resources.Res
import top.clientapp.generated.resources.client_saved_auth_required_message
import top.clientapp.generated.resources.client_saved_auth_required_title
import top.clientapp.generated.resources.client_saved_empty_description
import top.clientapp.generated.resources.client_saved_empty_title
import top.clientapp.generated.resources.client_saved_remove_content_description
import top.clientapp.generated.resources.client_saved_search_placeholder

@Composable
fun ClientSavedScreen(
    state: ClientSavedViewState,
    onEvent: (ClientSavedEvent) -> Unit,
    modifier: Modifier = Modifier
) {
    LazyColumn(
        modifier = modifier
            .fillMaxSize()
            .background(White),
        contentPadding = PaddingValues(start = 20.dp, top = 20.dp, end = 20.dp, bottom = 18.dp),
        verticalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        if (!state.requiresAuth) {
            item {
                AppSearchField(
                    value = state.searchQuery,
                    onValueChange = { onEvent(ClientSavedEvent.OnSearchChanged(it)) },
                    placeholder = stringResource(Res.string.client_saved_search_placeholder),
                    modifier = Modifier.fillMaxWidth()
                )
            }
        }

        when {
            state.requiresAuth -> {
                item {
                    ClientSavedEmptyState(
                        title = stringResource(Res.string.client_saved_auth_required_title),
                        description = stringResource(Res.string.client_saved_auth_required_message),
                        modifier = Modifier.fillMaxWidth()
                    )
                }
            }
            state.isLoading && state.venues.isEmpty() -> {
                item {
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(top = 40.dp),
                        contentAlignment = Alignment.Center
                    ) {
                        CircularProgressIndicator(color = Primary600)
                    }
                }
            }
            state.venues.isEmpty() -> {
                item {
                    ClientSavedEmptyState(
                        title = stringResource(Res.string.client_saved_empty_title),
                        description = stringResource(Res.string.client_saved_empty_description),
                        modifier = Modifier.fillMaxWidth()
                    )
                }
            }
            else -> {
                items(
                    items = state.venues,
                    key = { it.id }
                ) { venue ->
                    SavedVenueCard(
                        venue = venue,
                        onClick = { onEvent(ClientSavedEvent.OnVenueClicked(venue.id)) },
                        onFavoriteClick = { onEvent(ClientSavedEvent.OnFavoriteClicked(venue.id)) },
                        modifier = Modifier.fillMaxWidth()
                    )
                }
            }
        }
    }
}

@Composable
private fun SavedVenueCard(
    venue: VenueResponse,
    onClick: () -> Unit,
    onFavoriteClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    val shape = RoundedCornerShape(12.dp)

    Row(
        modifier = modifier
            .heightIn(min = 112.dp)
            .clip(shape)
            .clickable(onClick = onClick)
            .background(White)
            .border(1.dp, MineShaft100, shape)
            .padding(8.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        SavedVenueImage(
            venue = venue,
            modifier = Modifier.size(96.dp)
        )

        Spacer(modifier = Modifier.width(12.dp))

        Column(
            modifier = Modifier
                .weight(1f)
                .padding(vertical = 2.dp),
            verticalArrangement = Arrangement.Center
        ) {
            Text(
                text = venue.name,
                style = BodyLarge.copy(
                    color = MineShaft900,
                    fontSize = 16.sp,
                    lineHeight = 20.sp,
                    fontWeight = FontWeight.Medium
                ),
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )

            Spacer(modifier = Modifier.height(4.dp))

            Text(
                text = stringResource(venue.category.labelRes()),
                style = BodyMedium.copy(
                    color = MineShaft500,
                    fontSize = 12.sp,
                    lineHeight = 16.sp
                ),
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )

            Spacer(modifier = Modifier.height(8.dp))

            Row(
                horizontalArrangement = Arrangement.spacedBy(4.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Icon(
                    painter = AppIcons.RatingStarFilled,
                    contentDescription = null,
                    tint = Color.Unspecified,
                    modifier = Modifier.size(15.dp)
                )

                Text(
                    text = venue.ratingSummaryText(),
                    style = BodyMedium.copy(
                        color = MineShaft950,
                        fontSize = 12.sp,
                        lineHeight = 16.sp
                    ),
                    maxLines = 1
                )
            }

            Spacer(modifier = Modifier.height(6.dp))

            Row(
                horizontalArrangement = Arrangement.spacedBy(5.dp),
                verticalAlignment = Alignment.Top
            ) {
                Icon(
                    painter = AppIcons.VenueLocation,
                    contentDescription = null,
                    tint = MineShaft500,
                    modifier = Modifier.size(14.dp)
                )

                Text(
                    text = venue.location.address,
                    style = BodyMedium.copy(
                        color = MineShaft600,
                        fontSize = 12.sp,
                        lineHeight = 16.sp
                    ),
                    maxLines = 2,
                    overflow = TextOverflow.Ellipsis
                )
            }
        }

        Box(
            modifier = Modifier
                .size(40.dp)
                .clip(CircleShape)
                .clickable(onClick = onFavoriteClick),
            contentAlignment = Alignment.Center
        ) {
            Icon(
                painter = AppIcons.HeartFilled,
                contentDescription = stringResource(Res.string.client_saved_remove_content_description),
                tint = Color.Unspecified,
                modifier = Modifier.size(22.dp)
            )
        }
    }
}

@Composable
private fun SavedVenueImage(
    venue: VenueResponse,
    modifier: Modifier = Modifier
) {
    val imageUrl = venue.gallery.firstOrNull { !it.publicUrl.isNullOrBlank() }?.publicUrl
    Box(
        modifier = modifier
            .clip(RoundedCornerShape(8.dp))
            .background(MineShaft50),
        contentAlignment = Alignment.Center
    ) {
        if (imageUrl.isNullOrBlank()) {
            Icon(
                painter = AppIcons.VenueImagePlaceholder,
                contentDescription = null,
                tint = Color.Unspecified,
                modifier = Modifier.size(38.dp)
            )
        } else {
            AppNetworkImage(
                model = imageUrl,
                contentDescription = venue.name,
                contentScale = ContentScale.Crop,
                modifier = Modifier.fillMaxSize()
            )
        }
    }
}

@Composable
private fun ClientSavedEmptyState(
    title: String,
    description: String,
    modifier: Modifier = Modifier
) {
    Column(
        modifier = modifier.padding(top = 48.dp),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Text(
            text = title,
            style = BodyLarge.copy(
                color = MineShaft950,
                fontSize = 18.sp,
                lineHeight = 24.sp,
                fontWeight = FontWeight.SemiBold,
                textAlign = TextAlign.Center
            ),
            textAlign = TextAlign.Center
        )

        Spacer(modifier = Modifier.height(8.dp))

        Text(
            text = description,
            style = BodyMedium.copy(
                color = MineShaft600,
                fontSize = 14.sp,
                lineHeight = 20.sp,
                textAlign = TextAlign.Center
            ),
            textAlign = TextAlign.Center,
            modifier = Modifier.fillMaxWidth()
        )
    }
}
